<template>
  <q-page padding class="window-height window-width row justify-center items-center">
    <div class="column full-width">
      <div class="row">
        <div class="col-xs-12 col-sm-12 col-md-4 offset-md-4">

          <!-- Loading state -->
          <q-card v-if="loading" flat class="relative-position" style="background-color:transparent;height:120px">
            <q-inner-loading dark showing style="background-color:transparent;">
              <q-spinner-dots size="50px" color="secondary" />
            </q-inner-loading>
          </q-card>

          <!-- Error state -->
          <q-card v-else-if="error" dark bordered class="consent-card">
            <div class="consent-header">
              <img src="/android-chrome-192x192.png" alt="CapitalFlow" class="consent-logo" />
              <h5 class="text-h6 text-white q-mt-md q-mb-none">Authorization Error</h5>
            </div>
            <p class="text-body2 text-center text-negative q-px-lg">{{ error }}</p>
            <div class="q-px-lg q-pb-lg">
              <q-btn label="Go to Dashboard" color="secondary" class="full-width" @click="goHome" />
            </div>
          </q-card>

          <!-- Success state -->
          <q-card v-else-if="approved" dark bordered class="consent-card">
            <div class="consent-header">
              <img src="/android-chrome-192x192.png" alt="CapitalFlow" class="consent-logo" />
              <q-icon name="check_circle" size="48px" color="positive" class="q-mt-md" />
              <h5 class="text-h6 text-white q-mt-sm q-mb-xs">Authorization Approved</h5>
              <p class="text-body2 text-grey-5 q-mb-none">You can close this tab and return to your application.</p>
            </div>
          </q-card>

          <!-- Denied state -->
          <q-card v-else-if="denied" dark bordered class="consent-card">
            <div class="consent-header">
              <img src="/android-chrome-192x192.png" alt="CapitalFlow" class="consent-logo" />
              <q-icon name="cancel" size="48px" color="negative" class="q-mt-md" />
              <h5 class="text-h6 text-white q-mt-sm q-mb-xs">Authorization Denied</h5>
              <p class="text-body2 text-grey-5 q-mb-none">No access was granted. You can close this tab.</p>
            </div>
          </q-card>

          <!-- Consent screen -->
          <q-card v-else-if="authDetails" dark bordered class="consent-card">
            <!-- Logo + Header -->
            <div class="consent-header">
              <img src="/android-chrome-192x192.png" alt="CapitalFlow" class="consent-logo" />
              <h5 class="text-h6 text-white q-mt-md q-mb-xs">Authorize Connection</h5>
              <p class="text-body2 text-grey-5 q-mb-none">
                <strong class="text-white">{{ authDetails.client?.name || 'An application' }}</strong>
                wants to connect to your account
              </p>
            </div>

            <!-- Permissions -->
            <div class="consent-body">
              <div class="permission-section">
                <div class="permission-label text-grey-5 text-caption text-uppercase">
                  This will allow {{ authDetails.client?.name || 'this application' }} to
                </div>
                <div class="permission-item">
                  <q-icon name="check_circle" size="18px" color="positive" class="q-mr-sm" />
                  <span class="text-grey-3 text-body2">Access your CapitalFlow data via MCP tools</span>
                </div>
                <div class="permission-item">
                  <q-icon name="check_circle" size="18px" color="positive" class="q-mr-sm" />
                  <span class="text-grey-3 text-body2">Query trades, reports, and top contracts</span>
                </div>
                <div v-for="s in scopes" :key="s" class="permission-item">
                  <q-icon name="check_circle" size="18px" color="positive" class="q-mr-sm" />
                  <span class="text-grey-3 text-body2">{{ formatScope(s) }}</span>
                </div>
              </div>

              <q-separator dark class="q-my-sm" style="opacity: 0.15" />

              <div v-if="authDetails.redirect_uri" class="permission-section">
                <div class="permission-label text-grey-5 text-caption text-uppercase">
                  Redirect
                </div>
                <div class="permission-value">
                  <q-icon name="link" size="20px" color="grey-6" class="q-mr-sm" />
                  <span class="text-grey-4 text-caption redirect-uri">{{ authDetails.redirect_uri }}</span>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="consent-actions">
              <q-btn
                id="oauth-approve-btn"
                label="Approve"
                color="secondary"
                class="full-width"
                unelevated
                no-caps
                size="md"
                :loading="submitting"
                :disable="submitting"
                @click="onApprove"
              />
              <q-btn
                id="oauth-deny-btn"
                label="Deny"
                flat
                no-caps
                color="grey-5"
                class="full-width q-mt-sm"
                size="md"
                :disable="submitting"
                @click="onDeny"
              />
            </div>
          </q-card>

        </div>
      </div>
    </div>
  </q-page>
</template>

<script>
import { ref, computed, onMounted } from "vue"
import { useRouter, useRoute } from "vue-router"
import { useQuasar } from "quasar"
import { getClient } from "../../supabase"
import { loggerFactory } from "../../logger"

const logger = loggerFactory("OAuthConsentPage")

export default {
  name: "OAuthConsentPage",
  setup() {
    const $q = useQuasar()
    const isMobile = $q.platform.is.mobile
    const router = useRouter()
    const route = useRoute()

    const loading = ref(true)
    const submitting = ref(false)
    const error = ref(null)
    const authDetails = ref(null)
    const approved = ref(false)
    const denied = ref(false)

    const scopes = computed(() => {
      if (!authDetails.value?.scope) return []
      return authDetails.value.scope.trim().split(/\s+/).filter(Boolean)
    })

    const authorizationId = computed(() => route.query.authorization_id)

    const goHome = () => router.replace("/")

    const formatScope = (scope) => {
      const labels = {
        openid: "Sign in with your identity",
        profile: "Access your profile information",
        email: "View your email address",
        phone: "View your phone number",
      }
      return labels[scope] || scope
    }

    onMounted(async () => {
      if (!authorizationId.value) {
        error.value = "Missing authorization_id parameter."
        loading.value = false
        return
      }

      // Check if user is authenticated
      const supabase = getClient()
      const { data: { user } } = await supabase.auth.getUser()

      if (!user) {
        const consentUrl = `/oauth/consent?authorization_id=${encodeURIComponent(authorizationId.value)}`
        router.replace({ path: "/auth/login", query: { nextUrl: encodeURIComponent(consentUrl) } })
        return
      }

      // Fetch authorization details
      try {
        const { data, error: authError } = await supabase.auth.oauth.getAuthorizationDetails(authorizationId.value)

        if (authError || !data) {
          logger.error("Failed to get authorization details", authError)
          error.value = authError?.message || "Invalid or expired authorization request."
          loading.value = false
          return
        }

        authDetails.value = data
      } catch (e) {
        logger.error("Error fetching authorization details", e)
        error.value = "Unable to load authorization details."
      } finally {
        loading.value = false
      }
    })

    const onApprove = async () => {
      submitting.value = true
      try {
        const supabase = getClient()
        const { data, error: approveError } = await supabase.auth.oauth.approveAuthorization(authorizationId.value)

        if (approveError) {
          logger.error("Failed to approve authorization", approveError)
          $q.notify({ type: "negative", message: approveError.message || "Failed to approve authorization." })
          return
        }

        // Always show the confirmation state first
        approved.value = true

        // Redirect after a short delay so the user sees the confirmation
        if (data?.redirect_to) {
          setTimeout(() => {
            window.location.href = data.redirect_to
          }, 1500)
        }
      } catch (e) {
        logger.error("Error approving authorization", e)
        $q.notify({ type: "negative", message: "An error occurred while processing your request." })
      } finally {
        submitting.value = false
      }
    }

    const onDeny = async () => {
      submitting.value = true
      try {
        const supabase = getClient()
        const { data, error: denyError } = await supabase.auth.oauth.denyAuthorization(authorizationId.value)

        if (denyError) {
          logger.error("Failed to deny authorization", denyError)
          $q.notify({ type: "negative", message: denyError.message || "Failed to deny authorization." })
          return
        }

        // Always show the denied state first
        denied.value = true

        // Redirect after a short delay so the user sees the confirmation
        if (data?.redirect_to) {
          setTimeout(() => {
            window.location.href = data.redirect_to
          }, 1500)
        }
      } catch (e) {
        logger.error("Error denying authorization", e)
        $q.notify({ type: "negative", message: "An error occurred while processing your request." })
      } finally {
        submitting.value = false
      }
    }

    return {
      isMobile,
      loading,
      submitting,
      error,
      authDetails,
      scopes,
      approved,
      denied,
      goHome,
      formatScope,
      onApprove,
      onDeny
    }
  }
}
</script>

<style scoped>
.consent-card {
  border-radius: 16px !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  background: linear-gradient(165deg, rgba(30, 40, 60, 0.95) 0%, rgba(18, 24, 38, 0.98) 100%) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.04) !important;
  overflow: hidden;
}

.consent-header {
  text-align: center;
  padding: 32px 24px 20px;
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.06) 0%, transparent 100%);
}

.consent-logo {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.consent-body {
  padding: 4px 24px 8px;
}

.permission-section {
  padding: 10px 0;
}

.permission-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.8px;
  margin-bottom: 8px;
  opacity: 0.7;
}

.permission-value {
  display: flex;
  align-items: center;
}

.permission-item {
  display: flex;
  align-items: center;
  padding: 3px 0;
}

.redirect-uri {
  word-break: break-all;
  line-height: 1.4;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
}

.consent-actions {
  padding: 16px 24px 24px;
}
</style>
